import { API_CONFIG } from '../config/api.config';

export interface FileItem {
  name: string;
  url: string;
  size?: number;
  lastModified?: string;
  isDir?: boolean;
}

class StorageService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    return {
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async listFiles(path: string = ''): Promise<FileItem[]> {
    const url = API_CONFIG.getApiUrl(`/api/storage/files?path=${encodeURIComponent(path)}`);
    const response = await fetch(url, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur chargement fichiers');
    const data = await response.json();
    return data.files;
  }

  async createFolder(path: string): Promise<void> {
    const response = await fetch(API_CONFIG.getApiUrl('/api/storage/folder'), {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ path })
    });
    if (!response.ok) throw new Error('Erreur création dossier');
  }

  async uploadFile(file: File, path: string = '', customName?: string): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);
    // ✅ Ajout du paramètre si présent
    if (customName) {
        formData.append('customName', customName);
    }

    const response = await fetch(API_CONFIG.getApiUrl('/api/storage/upload'), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: formData
    });
    
    if (!response.ok) {
      if (response.status === 413) {
        throw new Error('Fichier trop volumineux pour le serveur.');
      }
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Erreur upload fichier');
    }
    const data = await response.json();
    return data.url;
  }

  async deleteFile(url: string): Promise<void> {
    const response = await fetch(API_CONFIG.getApiUrl('/api/storage/files'), {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ url })
    });

    if (!response.ok) throw new Error('Erreur suppression');
  }

  async deleteFolder(path: string): Promise<void> {
    const response = await fetch(API_CONFIG.getApiUrl('/api/storage/folder'), {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ path })
    });

    if (!response.ok) throw new Error('Erreur suppression dossier');
  }

  async copyItem(sourcePath: string, targetPath: string, isDir: boolean = false): Promise<void> {
    const response = await fetch(API_CONFIG.getApiUrl('/api/storage/copy'), {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sourcePath, targetPath, isDir })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Erreur copie');
    }
  }

  async moveItem(sourcePath: string, targetPath: string, isDir: boolean = false): Promise<void> {
    const response = await fetch(API_CONFIG.getApiUrl('/api/storage/move'), {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sourcePath, targetPath, isDir })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Erreur déplacement');
    }
  }

  async renameItem(oldPath: string, newPath: string): Promise<void> {
    // CORRECTION : S'assurer que l'URL pointe bien vers la route qu'on vient de créer
    // "/storage/rename" si le routeur est monté sur /api dans le backend
    const response = await fetch(API_CONFIG.getApiUrl('/storage/rename'), {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ oldPath, newPath })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Erreur renommage');
    }
  }

  async getTextFile(path: string): Promise<{ content: string; truncated?: boolean }> {
    const response = await fetch(API_CONFIG.getApiUrl(`/api/storage/text?path=${encodeURIComponent(path)}`), {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Erreur lecture fichier');
    }
    const data = await response.json();
    return { content: data.content || '', truncated: data.truncated };
  }

  // ✅ Méthode CORRIGÉE (Utilise fetch au lieu de api.post)
  async testS3Connection(): Promise<any> {
    // Adaptez l'URL si votre route backend est montée ailleurs (ex: /api/files/test-connection)
    // Ici je suppose qu'elle est au même endroit que /api/storage/files
    const response = await fetch(API_CONFIG.getApiUrl('/api/storage/test-connection'), {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
        // On tente de lire le message d'erreur JSON renvoyé par le backend
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || 'Erreur connexion S3');
    }
  }
}

export const storageService = new StorageService();
export default storageService;
